"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Image from "next/image";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  CLOSE_MOBILE_MENU_EVENT,
  FEATURE_TOUR_STEPS,
  OPEN_MOBILE_MENU_EVENT,
  type FeatureTourRole,
  type FeatureTourStep,
} from "@/constants/feature-tour";

const SHEET_OPEN_MS = 420;
const LG_BREAKPOINT = 1024;
const TOUR_STEP_KEY = "certlynx.feature-tour.step";

type Rect = { top: number; left: number; width: number; height: number };

function findVisibleTourTarget(id: string): HTMLElement | null {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${id}"]`));
  return (
    nodes.find((el) => {
      const r = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden") return false;
      return r.width > 2 && r.height > 2;
    }) ?? null
  );
}

function readRect(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function openMobileMenu() {
  window.dispatchEvent(new Event(OPEN_MOBILE_MENU_EVENT));
}

function closeMobileMenu() {
  window.dispatchEvent(new Event(CLOSE_MOBILE_MENU_EVENT));
}

export function FeatureTour({
  role,
  onComplete,
  preview = false,
}: {
  role: FeatureTourRole;
  onComplete: () => void;
  preview?: boolean;
}) {
  const steps = FEATURE_TOUR_STEPS[role] ?? [];
  const [index, setIndex] = useState(() => {
    if (typeof window === "undefined") return 0;
    const stored = Number(sessionStorage.getItem(TOUR_STEP_KEY));
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
  });
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const completing = useRef(false);

  const step: FeatureTourStep | undefined = steps[index];
  const isLast = index >= steps.length - 1;

  const persistComplete = useCallback(() => {
    if (completing.current) return;
    completing.current = true;
    closeMobileMenu();
    if (!preview) {
      fetch("/api/user/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ featureTourCompleted: true }),
      }).catch(() => {});
    }
    try {
      sessionStorage.removeItem(TOUR_STEP_KEY);
    } catch {}
    onComplete();
  }, [onComplete, preview]);

  const measure = useCallback(() => {
    if (!step) return;
    const el = findVisibleTourTarget(step.target);
    setTargetRect(el ? readRect(el) : null);
  }, [step]);

  useEffect(() => {
    if (!step) return;
    let cancelled = false;
    const isDesktop = window.innerWidth >= LG_BREAKPOINT;

    if (!isDesktop && step.mobileSheet) {
      openMobileMenu();
    } else {
      closeMobileMenu();
    }

    const wait = !isDesktop && step.mobileSheet ? SHEET_OPEN_MS : !isDesktop ? 320 : 50;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      measure();
    }, wait);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [step, measure]);

  useLayoutEffect(() => {
    if (!step) return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [step, measure]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") persistComplete();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [persistComplete]);

  useEffect(() => {
    popupRef.current?.querySelector<HTMLButtonElement>("[data-tour-next]")?.focus();
  }, [index]);

  if (!step) return null;

  const pad = 6;
  const hole = targetRect
    ? {
        top: Math.max(0, targetRect.top - pad),
        left: Math.max(0, targetRect.left - pad),
        width: targetRect.width + pad * 2,
        height: targetRect.height + pad * 2,
      }
    : null;

  const popupWidth = Math.min(320, typeof window !== "undefined" ? window.innerWidth - 24 : 320);
  const popupEstHeight = 210;
  const gap = 12;
  let popupTop = 80;
  let popupLeft = 12;
  if (hole) {
    const lowerHalf = hole.top > (typeof window !== "undefined" ? window.innerHeight * 0.55 : 400);
    popupTop = lowerHalf ? hole.top - popupEstHeight - gap : hole.top + hole.height + gap;
    popupTop = Math.max(12, Math.min(popupTop, window.innerHeight - popupEstHeight - 12));
    popupLeft = hole.left + hole.width / 2 - popupWidth / 2;
    popupLeft = Math.max(12, Math.min(popupLeft, window.innerWidth - popupWidth - 12));
  }

  const goNext = () => {
    if (isLast) {
      persistComplete();
      return;
    }
    setIndex((i) => {
      const next = i + 1;
      try {
        sessionStorage.setItem(TOUR_STEP_KEY, String(next));
      } catch {}
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[90] pointer-events-none" role="presentation">
      {hole ? (
        <>
          <div className="absolute bg-slate-900/45 pointer-events-auto" style={{ top: 0, left: 0, right: 0, height: hole.top }} />
          <div className="absolute bg-slate-900/45 pointer-events-auto" style={{ top: hole.top, left: 0, width: hole.left, height: hole.height }} />
          <div className="absolute bg-slate-900/45 pointer-events-auto" style={{ top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height }} />
          <div className="absolute bg-slate-900/45 pointer-events-auto" style={{ top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }} />
          <div
            className="absolute rounded-lg ring-2 ring-blue-500 ring-offset-2 ring-offset-transparent pointer-events-none"
            style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-slate-900/45 pointer-events-auto" />
      )}

      <div
        ref={popupRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feature-tour-title"
        className="absolute pointer-events-auto bg-white border border-gray-200 rounded-2xl shadow-lg p-4"
        style={{ top: popupTop, left: popupLeft, width: popupWidth }}
      >
        <button
          type="button"
          onClick={persistComplete}
          aria-label="Close tour"
          className="absolute right-3 top-3 rounded p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 pr-6 mb-3">
          <Image
            src="/main-navigation-logo.png"
            alt="CertLynx"
            width={158}
            height={30}
            className="h-6 w-auto"
          />
        </div>

        <h2 id="feature-tour-title" className="text-sm font-semibold text-gray-900">
          {step.title}
        </h2>
        <p className="text-sm text-gray-600 mt-1 leading-snug">{step.body}</p>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1.5" aria-label={`Step ${index + 1} of ${steps.length}`}>
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 rounded-full ${i === index ? "w-4 bg-blue-600" : "w-1.5 bg-gray-200"}`}
              />
            ))}
          </div>
          <Button size="sm" data-tour-next onClick={goNext} className="h-8 px-4 rounded-md">
            {isLast ? "Got it" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}
